import { useEffect, useRef, useState, createContext, useCallback } from "react";
import PropTypes from "prop-types";

import JWPlayerAdapter from "./Adapters/JWPlayer";

export const PlayerContext = createContext();
PlayerContext.displayName = "PlayerContext";

export default function Player({
  children,
  options,
  defaultAdvertisingOptions,
  onError,
  onAdComplete,
  onAdStart,
}) {
  const [playerAdapter, setPlayerAdapter] = useState();
  const containerRef = useRef(null);
  const isInitializingRef = useRef(false);
  const { autostart = false, mute = false } = options;
  const { adTag } = defaultAdvertisingOptions;

  useEffect(() => {
    let unsubscribe = () => {};
    if (typeof onAdComplete === "function" && playerAdapter) {
      unsubscribe = playerAdapter.onAdComplete(onAdComplete);
    }

    return () => {
      unsubscribe();
    };
  }, [onAdComplete, playerAdapter]);

  useEffect(() => {
    let unsubscribe = () => {};
    if (typeof onAdStart === "function" && playerAdapter) {
      unsubscribe = playerAdapter.onAdStart(onAdStart);
    }

    return () => {
      unsubscribe();
    };
  }, [onAdStart, playerAdapter]);

  useEffect(
    () => () => {
      if (playerAdapter) {
        playerAdapter.remove();
      }
    },
    [playerAdapter],
  );

  useEffect(() => {
    if (playerAdapter) {
      playerAdapter.setAutostart(autostart);
    }
  }, [autostart, playerAdapter]);

  useEffect(() => {
    if (playerAdapter) {
      playerAdapter.setMute(mute);
    }
  }, [mute, playerAdapter]);

  const initializePlayer = useCallback(
    async (source) => {
      try {
        if (!playerAdapter) {
          const adapter = new JWPlayerAdapter(containerRef.current, {
            adTag,
            source,
          });
          await adapter.setup();
          setPlayerAdapter(adapter);
        }
      } catch (e) {
        onError(e);
      }
    },
    [adTag, onError, playerAdapter],
  );

  const load = useCallback(
    async (source) => {
      if (playerAdapter) {
        playerAdapter.load(source);
      } else if (!playerAdapter && isInitializingRef.current === false) {
        isInitializingRef.current = true;
        await initializePlayer(source);
        isInitializingRef.current = false;
      }
    },
    [initializePlayer, playerAdapter],
  );

  return (
    <PlayerContext.Provider
      value={{ ...playerAdapter, isReady: !!playerAdapter, load, containerRef }}
    >
      {children}
    </PlayerContext.Provider>
  );
}

Player.propTypes = {
  options: PropTypes.shape({
    autostart: PropTypes.bool,
    mute: PropTypes.bool,
  }),
  defaultAdvertisingOptions: PropTypes.shape({
    adTag: PropTypes.string,
  }),
  onError: PropTypes.func,
  onAdComplete: PropTypes.func,
  onAdStart: PropTypes.func,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
};

Player.defaultProps = {
  options: {},
  defaultAdvertisingOptions: {},
  onError: () => {},
  onAdComplete: undefined,
  onAdStart: undefined,
  children: null,
};
